

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewCreatorOnboardingModal from 'components/NewCreatorOnboardingModal';

const CreatorOnboardings = () => {
    const [onboardings, setOnboardings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('creator_onboardings');

    useEffect(() => {
        fetchOnboardings();
    }, []);

    const fetchOnboardings = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/creator-onboardings/');
            setOnboardings(response.data);
        } catch (error) {
            console.error('Error fetching creator onboardings:', error);
            setSnackbar({ open: true, message: 'Error loading creator onboardings', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveOnboarding = async (payload) => {
        try {
            await axiosInstance.post('/creator-onboardings/', payload);
            setSnackbar({ open: true, message: 'Creator Onboarding created successfully', severity: 'success' });
            fetchOnboardings();
        } catch (error) {
            console.error('Error saving creator onboarding:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/creator-onboarding/${rowData.id}`);
    };

    const getFullName = (row) => {
        const parts = [row.first_name, row.middle_name, row.last_name].filter(Boolean);
        return parts.join(' ') || '-';
    };

    const columns = [
        {
            Header: "Name",
            accessor: "first_name",
            width: "20%",
            Cell: ({ row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {getFullName(row.original)}
                </MDTypography>
            )
        },
        { Header: "Email", accessor: "email", width: "20%" },
        { Header: "Phone", accessor: "phone", width: "15%" },
        { Header: "Location", accessor: "location", width: "15%" },
        { Header: "Influencer Category", accessor: "influencer_category", width: "15%" },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "15%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: onboardings,
    }), [onboardings]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Creator Onboardings</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Onboarding
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : onboardings.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Creator Onboarding" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewCreatorOnboardingModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveOnboarding}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default CreatorOnboardings;

